import React, { useEffect, useState } from "react";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import "../../Assets/Styles/components/pricingRedesign.scss";
import SignIn from "../SignIn/signIn";
import ContactUsModal from "../ContactUsModal/contactUsModal";
import { auth } from "../../Config/firebase-config";
import {
  getCurrentSubscriptionStatus,
  hasPaddleConfiguration,
  refreshAfterSuccessfulSubscription,
  startPaddleSubscriptionCheckout,
} from "../../actions/Billing/Paddle";
import type { AppDispatch, RootState } from "../../Redux/store";

const Plans: React.FC = () => {
  const [billingPlan, setBillingPlan] = useState<"monthly" | "yearly">("monthly");
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openContactUs, setOpenContactUs] = useState(false);
  const [checkoutLoader, setCheckoutLoader] = useState(false);
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  const isLoggedIn = Boolean(Object.keys(userSliceState.user_Auth ?? {}).length);
  const userEmail =
    typeof userSliceState.user_Auth?.email === "string"
      ? userSliceState.user_Auth.email
      : auth.currentUser?.email ?? undefined;

  useEffect(() => {
    let active = true;

    const syncSubscriptionStatus = async () => {
      if (!isLoggedIn) {
        if (active) setHasPremiumAccess(false);
        return;
      }
      try {
        const status = await getCurrentSubscriptionStatus(false);
        if (active) setHasPremiumAccess(status.isPremium);
      } catch {
        if (active) setHasPremiumAccess(false);
      }
    };

    void syncSubscriptionStatus();

    return () => {
      active = false;
    };
  }, [isLoggedIn, userSliceState.user_Auth]);

  const handleFreeAction = () => {
    const userAgent =
      navigator.userAgent ||
      (navigator as { vendor?: string }).vendor ||
      (window as { opera?: string }).opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as { MSStream?: unknown }).MSStream;
    const isAndroid = /Android/.test(userAgent);

    if (isIOS) {
      window.location.href = "https://apps.apple.com/us/app/ezy-watermark-photos-lite/id494473910";
    } else if (isAndroid) {
      window.location.href = "https://play.google.com/store/apps/details?id=com.whizpool.ezywatermarklite";
    } else {
      history.push("/home");
    }
  };

  const handlePremiumCheckout = async (planType: "monthly" | "yearly") => {
    if (!hasPaddleConfiguration()) {
      message.error(
        "Paddle is not configured yet. Add the client token and monthly/yearly price IDs to your environment first."
      );
      return;
    }

    if (!isLoggedIn) {
      setOpenSignIn(true);
      return;
    }

    if (hasPremiumAccess) {
      message.info("Your premium subscription is already active on this account.");
      return;
    }

    const loadingKey = `paddle-checkout-${planType}`;

    setCheckoutLoader(true);
    try {
      const checkoutEvent = await startPaddleSubscriptionCheckout({
        plan: planType,
        email: userEmail,
        userId: auth.currentUser?.uid,
      });

      if (checkoutEvent.name !== "checkout.completed") return;

      message.loading({
        key: loadingKey,
        content: "Confirming your subscription...",
        duration: 0,
      });

      const refreshedStatus = await refreshAfterSuccessfulSubscription(dispatch);

      if (refreshedStatus.isPremium) {
        setHasPremiumAccess(true);
        message.success({
          key: loadingKey,
          content: "Premium subscription activated successfully.",
          duration: 4,
        });
        history.push("/home");
      } else {
        message.success({
          key: loadingKey,
          content:
            "Payment completed. Premium access should appear shortly after the subscription sync finishes.",
          duration: 6,
        });
      }
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Unable to start Paddle checkout."
      );
    } finally {
      setCheckoutLoader(false);
    }
  };

  const getPremiumButtonText = () => {
    if (checkoutLoader) return "Processing...";
    if (hasPremiumAccess) return "Premium Plan Active";
    if (billingPlan === "monthly") return "Choose Monthly";
    return "Choose Yearly";
  };

  return (
    <div className="pricing-redesign">
      <div className="cards">
        {/* FREE CARD */}
        <div className="card">
          <div className="badge">FLEXIBLE</div>
          <div className="plan-name">Free</div>
          <div className="price">
            $0 <span className="unit">/mo</span>
          </div>
          <button type="button" className="cta free-cta" onClick={handleFreeAction}>
            Get Started
          </button>
          <ul>
            <li>
              <span className="tick">&#10003;</span> Use watermark templates
            </li>
            <li>
              <span className="tick">&#10003;</span> Access 300+ fonts
            </li>
            <li>
              <span className="tick">&#10003;</span> Export files to other apps
            </li>
            <li>
              <span className="tick">&#10003;</span> Download watermarks in high-res
            </li>
          </ul>
        </div>

        {/* PREMIUM CARD (Monthly / Yearly combined) */}
        <div className="card premium">
          <div className="badge">MOST POPULAR</div>
          <div className="plan-name">Premium</div>

          <div className="billing-toggle" id="billingToggle">
            <button
              type="button"
              className={billingPlan === "monthly" ? "active" : ""}
              onClick={() => setBillingPlan("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billingPlan === "yearly" ? "active" : ""}
              onClick={() => setBillingPlan("yearly")}
            >
              Yearly <span className="save-pill">-16%</span>
            </button>
          </div>

          <div className="price" id="premiumPrice">
            {billingPlan === "monthly" ? (
              <>
                $1.99 <span className="unit">/mo</span>
              </>
            ) : (
              <>
                $19.99 <span className="unit">/yr</span>
              </>
            )}
          </div>

          <button
            type="button"
            className="cta premium-cta"
            id="premiumCta"
            onClick={() => void handlePremiumCheckout(billingPlan)}
            disabled={checkoutLoader || hasPremiumAccess}
          >
            {getPremiumButtonText()}
          </button>

          <ul>
            <li>
              <span className="tick">&#10003;</span> Unlimited batch watermarking
            </li>
            <li>
              <span className="tick">&#10003;</span> Access to Photo and Video apps
            </li>
            <li>
              <span className="tick">&#10003;</span> Sync templates across platforms
            </li>
            <li>
              <span className="tick">&#10003;</span> Maintain original resolutions
            </li>
            <li>
              <span className="tick">&#10003;</span> Use all recent logos &amp; templates
            </li>
            <li>
              <span className="tick">&#10003;</span> Insert more than 3 watermarks at once
            </li>
            <li>
              <span className="tick">&#10003;</span> Insert Date/Time in autograph
            </li>
            <li>
              <span className="tick">&#10003;</span> Save autograph &middot; No ads
            </li>
          </ul>
        </div>

        {/* ENTERPRISE CARD */}
        <div className="card">
          <div className="badge flexible">FLEXIBLE</div>
          <div className="plan-name">Enterprise</div>
          <div className="price">Custom</div>
          <button
            type="button"
            className="cta enterprise-cta"
            onClick={() => setOpenContactUs(true)}
          >
            Contact Sales
          </button>
          <ul>
            <li>
              <span className="tick">&#10003;</span> Everything in Premium
            </li>
            <li>
              <span className="tick">&#10003;</span> Cloud template sync
            </li>
            <li>
              <span className="tick">&#10003;</span> Priority updates &amp; support
            </li>
            <li>
              <span className="tick">&#10003;</span> Access on all your devices
            </li>
            <li>
              <span className="tick">&#10003;</span> No ads &middot; No sticker purchases
            </li>
            <li>
              <span className="tick">&#10003;</span> Best value for teams
            </li>
          </ul>
        </div>
      </div>

      <div className="pricing-footer">
        <span className="pricing-footer__icon" aria-hidden="true">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <span>
          <strong>Secure checkout.</strong> Your payment information is 100% safe with us.
        </span>
      </div>

      <SignIn
        open={openSignIn}
        setOpen={setOpenSignIn}
        onSuccess={() => {
          void handlePremiumCheckout(billingPlan);
        }}
      />

      <ContactUsModal open={openContactUs} setOpen={setOpenContactUs} />
    </div>
  );
};

export default Plans;
